import * as Sentry from "@sentry/react";
import { isSessionExpiredError } from "@utils/authErrors";
import { getApiErrorMeta, getApiErrorMessage } from "@utils/errorHandler";
import { showToast, ToastType } from "@utils/toast";

/**
 * Amplify/Cognito exception names raised by ordinary user input during sign-in,
 * password reset, and email verification. These are shown to the user as toasts
 * and must not be reported to Sentry as application errors.
 */
const EXPECTED_AUTH_ERROR_NAMES = new Set<string>([
  "NotAuthorizedException",
  "UserNotFoundException",
  "UserNotConfirmedException",
  "UsernameExistsException",
  "CodeMismatchException",
  "ExpiredCodeException",
  "InvalidPasswordException",
  "PasswordResetRequiredException",
  "LimitExceededException"
]);

/**
 * Backend error codes (response.data.errorCode/code) that represent an expected,
 * handled outcome — an authorization denial or a request the user must correct —
 * not an application fault. These are shown to the user as a toast and must not
 * be reported to Sentry. Covers report categories #3 (auth) and #4 (validation).
 */
const EXPECTED_API_ERROR_CODES = new Set<string>([
  "FORBIDDEN",
  "UNAUTHORIZED",
  "AUTH_REQUIRED",
  "VALIDATION_FAILED"
]);

/** HTTP statuses that are expected authorization outcomes, not app faults. */
const EXPECTED_AUTH_STATUSES = new Set<number>([401, 403]);

/** Spec default for trace sampling (observability map #2763/#2764). */
export const TRACES_SAMPLE_RATE_DEFAULT = 0.15;

/**
 * Trace sample rate from deploy config, so the rate is tunable per environment
 * without a code change. Anything missing, empty, unparseable, or outside 0–1
 * falls back to the default — a typo'd env var must not silently switch tracing
 * off, which is the one failure this code exists to avoid.
 *
 * Only transactions are sampled. Error events are not gated by this.
 */
export const parseTracesSampleRate = (raw?: string): number => {
  // Trimmed first: Number(" ") is 0, and " " is truthy, so an all-whitespace
  // value would otherwise pass every check below and switch tracing off — the
  // exact silent-disable this function exists to prevent.
  const trimmed = raw?.trim();
  const rate = Number(trimmed);
  return trimmed && Number.isFinite(rate) && rate >= 0 && rate <= 1
    ? rate
    : TRACES_SAMPLE_RATE_DEFAULT;
};

/**
 * Anchored on the filename, so `…/newsw.js` and `…/sw.js.map` cannot match, but
 * a bare or normalised frame (`registerSW.js`, `app:///sw.js`) still does.
 * Case-sensitive on purpose: `registerSW.js` is the emitted artefact name, and
 * an `/i` here would re-widen the match.
 */
const SW_FILE = /(^|\/)(registerSW|sw)\.js(\?|#|$)/;

/** The two variants that carry a usable message (FRONTEND-11X, FRONTEND-130). */
const SW_MESSAGE =
  /Failed to register a ServiceWorker|Script\s+\S*\/sw\.js\b.*load failed/i;

/**
 * Whether the event is a service-worker registration failure.
 *
 * `vite-plugin-pwa` injects its own registration snippet (`injectRegister`
 * defaults to "auto" and vite.config.mjs does not set it) and that snippet calls
 * `navigator.serviceWorker.register()` with no `.catch()`, so every refusal
 * becomes an unhandled rejection. Its message is the bare string "Rejected"
 * (FRONTEND-113, 36 users in production), too generic to match on text — hence
 * matching the frame. Browsers refuse registration routinely and the app runs
 * without the worker, so this is not an application fault.
 *
 * ponytail: dropping the report, not fixing the missing `.catch()`. Owning the
 * registration (`injectRegister: null` plus `registerSW()` from
 * `virtual:pwa-register`, which has an `onRegisterError` hook) would stop the
 * rejection at source and is the better fix — but it changes how the app
 * installs and updates, which wants verifying in a browser rather than inferring
 * from a Sentry issue.
 */
export const isServiceWorkerRegistrationEvent = (
  event: Sentry.ErrorEvent
): boolean =>
  // Across every value, not just the first: a chained error (`cause`, an
  // AggregateError) arrives as several, and the registration failure need not
  // be at index 0.
  event.exception?.values?.some(
    (value) =>
      value.stacktrace?.frames?.some((frame) =>
        SW_FILE.test(frame.filename ?? "")
      ) || SW_MESSAGE.test(value.value ?? "")
  ) ?? false;

/**
 * Whether a captured error is known low-signal noise that should be dropped in
 * Sentry's `beforeSend` rather than reported. Covers stale-deploy chunk loads,
 * benign browser internals, mail-scanner/extension-injected errors, the expected
 * Cognito auth failures that are normal user mistakes, handled 401/403
 * authorization denials, client/server validation failures, and transient
 * client-side network blips — none of which are actionable application faults.
 */
export const shouldDropSentryEvent = (error: unknown): boolean => {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : "";
  const name = error instanceof Error ? error.name : "";

  // Expired/revoked session detected mid-request. Expected condition — the user
  // is signed out and redirected to login; not an application fault. FRONTEND-YK.
  if (isSessionExpiredError(error)) {
    return true;
  }

  // Chunk load errors fire on every deploy when a client holds a stale index
  // referencing now-removed hashed assets; the app self-recovers.
  if (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  ) {
    return true;
  }

  // Expected Amplify/Cognito auth errors (wrong password, unknown user,
  // bad/expired code, weak password). FRONTEND-DT/DR/V1/V3/V9/WP/...
  if (EXPECTED_AUTH_ERROR_NAMES.has(name)) {
    return true;
  }

  // InvalidParameterException is only noise for the password-policy regex (user
  // typed spaces / a too-weak password). Other uses may be genuine bugs.
  if (
    name === "InvalidParameterException" &&
    /previousPassword|proposedPassword|satisfy regular expression/i.test(
      message
    )
  ) {
    return true;
  }

  // ResizeObserver loop warnings are benign browser internals.
  if (message.includes("ResizeObserver loop")) {
    return true;
  }

  // Injected by mail-scanner/browser extensions (e.g. Outlook SafeLink), not
  // our code. FRONTEND-XP.
  if (message.includes("Object Not Found Matching Id")) {
    return true;
  }

  const { code, status } = getApiErrorMeta(error);

  // Handled authorization denials and validation failures. These are surfaced
  // to the user as a toast and are expected outcomes, not application faults.
  // Report categories #3 (BACKEND-ZY "User is not admin", FORBIDDEN/Unauthorized
  // 401/403) and #4 (VALIDATION_FAILED form validation).
  if (code && EXPECTED_API_ERROR_CODES.has(code)) {
    return true;
  }
  if (status !== undefined && EXPECTED_AUTH_STATUSES.has(status)) {
    return true;
  }
  if (
    /\bUnauthorized\b/i.test(message) ||
    /User is not admin/i.test(message) ||
    /\bValidation failed\b/i.test(message)
  ) {
    return true;
  }

  // Transient client-side connectivity blips. Not actionable as JS errors — the
  // request-storm that previously generated these in bulk (the create-package
  // render loop, FRONTEND-Z4/Z5/Z6) is fixed at source; residual single events
  // are individual users dropping offline. Report category #5.
  if (
    name === "NetworkError" ||
    /A network error has occurred/i.test(message) ||
    /Failed to fetch/i.test(message)
  ) {
    return true;
  }

  return false;
};

/**
 * Error context options for enhanced error tracking
 */
export interface ErrorContext {
  /** Custom tags for categorizing errors */
  tags?: Record<string, string>;
  /** Additional context data */
  extra?: Record<string, unknown>;
  /** Error level (default: error) */
  level?: Sentry.SeverityLevel;
}

/**
 * Captures an error and sends it to Sentry with optional context
 * @param error - The error to capture
 * @param muteToast - Whether to suppress the toast notification (default: false)
 * @param context - Optional additional context for the error
 */
export const catchSentryError = (
  error: unknown,
  muteToast = false,
  context?: ErrorContext
) => {
  // Expired/revoked session: leave a breadcrumb but do not report as an error —
  // the auth layer signs the user out, surfaces the user-facing message, and the
  // app recovers by sending them to login. The toast is owned by useAuthContext
  // (once per expiry), not here, so it is not duplicated per failing request.
  // FRONTEND-YK.
  if (isSessionExpiredError(error)) {
    Sentry.addBreadcrumb({
      category: "auth",
      data: context?.tags,
      level: "info",
      message: "Session expired during API call — signing user out"
    });
    return undefined;
  }

  if (!muteToast) {
    const errorMessage = getApiErrorMessage(error);
    showToast(errorMessage, ToastType.Error);
  }

  // Capture full technical error in Sentry with API context
  return Sentry.withScope((scope) => {
    // Enrich via the shared extractor. This block used to read the axios shape
    // directly, so every error that survived the filter — the genuine 5xx we
    // actually want to act on — arrived with statusCode, responseData,
    // errorCode and requestId all undefined.
    const { code, data, requestId, status } = getApiErrorMeta(error);
    if (status !== undefined || data !== undefined) {
      scope.setExtra("statusCode", status);
      scope.setExtra("responseData", data);
      scope.setExtra("errorCode", code);
      scope.setExtra("requestId", requestId);
    }

    // Add custom context if provided
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    if (context?.level) {
      scope.setLevel(context.level);
    }

    return Sentry.captureException(error);
  });
};

/**
 * Sets the current user context in Sentry for better error tracking
 * Call this when user logs in
 */
export const setSentryUser = (user: {
  id: string;
  email?: string;
  username?: string;
  organisationId?: string;
  organisationName?: string;
}) => {
  Sentry.setUser({
    email: user.email,
    id: user.id,
    username: user.username
  });

  // Set organisation as a tag for filtering
  if (user.organisationId) {
    Sentry.setTag("organisation.id", user.organisationId);
  }
  if (user.organisationName) {
    Sentry.setTag("organisation.name", user.organisationName);
  }
};

/**
 * Clears the user context from Sentry
 * Call this when user logs out
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTag("organisation.id", undefined);
  Sentry.setTag("organisation.name", undefined);
};

/**
 * Add a breadcrumb to help trace user actions leading to an error
 */
export const addSentryBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
) => {
  Sentry.addBreadcrumb({
    category,
    data,
    level,
    message
  });
};
